import React from 'react';
import { View, Heading, Text, Flex, Image } from '@adobe/react-spectrum';
import { Button, Switch, DialogTrigger, Dialog } from '@adobe/react-spectrum';
import { ActionButton, Content, ButtonGroup } from '@adobe/react-spectrum';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { ThemeContext } from 'context/themeContext';
import { ThemeContextType } from 'types/theme';
import HomeIcon from '../../assets/svgIcons/HomeIcon';
import RoadmapIcon from '../../assets/svgIcons/RoadmapIcon';
import MilestoneIcon from '../../assets/svgIcons/MilestoneIcon';
import CodeNameIcon from '../../assets/svgIcons/CodeNameIcon';
import SettingsIcon from '../../assets/svgIcons/SettingsIcon';
import LeftSidebarOpenIcon from '../../assets/svgIcons/LeftSidebarOpenIcon';
import LeftSidebarCloseIcon from '../../assets/svgIcons/LeftSidebarCloseIcon';
import TargetIcon from '../../assets/svgIcons/TargetIcon';
import ChevronDownIcon from '../../assets/svgIcons/ChevronDownIcon';
import SavedViewShareLinkIcon from 'app/assets/svgIcons/SavedViewShareLinkIcon';
import styles from './leftSidebar.module.scss';
import { useLayoutSlice } from 'app/pages/Layout/slice';
import { selectLayout } from 'app/pages/Layout/slice/selectors';
import {
  selectHierarchyFilters,
  selectAudiences,
  selectMustWinFilterOptions,
  selectMustWinView,
  selectCategories,
  selectProductGroups,
  selectProductFilter,
  selectCategoryProductFilter,
  selectMustWinDropdownSelection,
  selectCategoryToProductMap,
  selectDateRange
} from 'app/pages/Home/slice/selectors';
import { useHomeSlice } from 'app/pages/Home/slice';
import { selectHasPendingApprovals } from 'app/store/pendingApprovalsSlice';
import { saveUserPreferences, getUserPreferences } from 'utils/helpers';
import { shouldPreferUrlOverLocalPreferences } from 'utils/sharedViewMode';
import { copyMustWinDeepLink } from 'utils/mustWinDeepLink';
import { buildSharedViewNavigationPath } from 'utils/viewStateDeepLink';
import { useViewState } from 'app/context/ViewStateContext';
import { Tooltip, TooltipTrigger, ToastQueue } from '@adobe/react-spectrum';
import { SavedViewsSection, SaveViewModal } from 'app/components/SavedViews';
import {
  registerSaveViewModalBridge,
  type SaveViewModalMode
} from 'app/components/SavedViews/saveViewModalBridge';
import { useSavedViewActions } from 'app/components/SavedViews/SavedViewsSection/hooks/useSavedViewActions';

// Module-level (not component-instance) — restore Must Wins from localStorage
// once per browser session, not on every LeftSidebar mount. LeftSidebar
// remounts on every Milestones<->Roadmap transition (separate top-level
// pages), so a per-mount effect would re-assert a possibly-stale
// mustWinViewActive right after a Saved View (or My Stuff) had already
// correctly set it — changing the divergence fingerprint and causing the
// Saved View to be silently exited moments after being applied.
let hasRestoredMustWinPreference = false;

const LeftSidebar = () => {
  const [saveViewModalMode, setSaveViewModalMode] =
    React.useState<SaveViewModalMode>(null);

  const { theme, changeTheme } = React.useContext(
    ThemeContext
  ) as ThemeContextType;
  const { actions } = useLayoutSlice();
  const { actions: homeActions } = useHomeSlice();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const getCurrentViewState = useViewState();
  const { isSidebarOpen, isMustWinsExpanded } = useSelector(selectLayout);
  const hierarchyFilters = useSelector(selectHierarchyFilters);
  const audiences = useSelector(selectAudiences);
  const mustWinFilterOptions = useSelector(selectMustWinFilterOptions);
  const categories = useSelector(selectCategories);
  const productGroups = useSelector(selectProductGroups);
  const productFilter = useSelector(selectProductFilter);
  const categoryProductFilter = useSelector(selectCategoryProductFilter);
  const mustWinViewActive = useSelector(selectMustWinView);
  const mustWinDropdownSelection = useSelector(selectMustWinDropdownSelection);
  const categoryToProductMap = useSelector(selectCategoryToProductMap);
  const dateRange = useSelector(selectDateRange);
  // Single instance of this hook for the whole nav tree — SavedViewsSection
  // (rendered below) consumes these via props rather than calling the hook
  // itself, so there is exactly one copy of the active-view state machine
  // (opening grace period, filter baseline, divergence/deleted effects)
  // instead of two independently-effected instances racing each other.
  const {
    activeSavedViewId,
    exitActiveSavedView,
    handleCopyLink,
    handleOpenSavedView,
    handleOpenMyStuff
  } = useSavedViewActions();

  // Determine active route
  const isReviewApprovalActive = location.pathname === '/review-approval';
  // Backend response (GET /api/v2/approvals/pending-count, fetched at app
  // startup and refreshed on each /review-approval visit - see
  // pendingApprovalsSlice.ts) is the sole source of truth for this
  // indicator - no locally-computed "viewed"/dismissed override.
  const hasPendingApprovals = useSelector(selectHasPendingApprovals);
  const isRoadmapActive =
    (location.pathname === '/' ||
      location.pathname === '/roadmap' ||
      location.pathname.startsWith('/moments')) &&
    !mustWinViewActive &&
    !activeSavedViewId;
  // A Saved View can itself be a Must Wins view or point at /milestones - its
  // own row in the Saved Views list already shows the highlight, so exclude
  // activeSavedViewId here to avoid double-highlighting both the Saved View
  // row and the Must Wins/Milestones nav icon at the same time.
  const isMilestonesActive =
    location.pathname === '/milestones' && !activeSavedViewId;
  const isMustWinsActive = mustWinViewActive && !activeSavedViewId;

  const navigateWithSharedViewState = React.useCallback(
    (targetPath: string) => {
      navigate(
        buildSharedViewNavigationPath(
          targetPath,
          location.search,
          getCurrentViewState?.() ?? null
        )
      );
    },
    [getCurrentViewState, location.search, navigate]
  );

  // My Stuff is a Roadmap-only view (rendered inside Roadmap.tsx), same as
  // Must Wins above — navigate there when selected from another page (e.g.
  // Milestones Grid) so it actually switches to it instead of silently
  // setting state on the current page.
  const handleOpenMyStuffFromNav = React.useCallback(() => {
    handleOpenMyStuff();
    const isOnRoadmapPage =
      location.pathname === '/' ||
      location.pathname === '/roadmap' ||
      location.pathname.startsWith('/moments');
    if (!isOnRoadmapPage) {
      navigateWithSharedViewState('/roadmap');
    }
  }, [handleOpenMyStuff, location.pathname, navigateWithSharedViewState]);

  // Must Wins dropdown expand/collapse - lives in Redux (isMustWinsExpanded
  // above) so it survives LeftSidebar remounts instead of resetting to its
  // default, independently of the sidebar's own open/collapsed state.
  const mustWinsRef = React.useRef<HTMLDivElement>(null);

  // Add tooltip state management similar to TimelineCard
  const [openTooltip, setOpenTooltip] = React.useState<string | null>(null);
  const tooltipTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // State for link tooltip (separate from other tooltips)
  const [linkTooltipOpen, setLinkTooltipOpen] = React.useState<string | null>(
    null
  );
  const linkTooltipTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Add tooltip handlers similar to TimelineCard
  const handleTooltipMouseEnter = (key: string) => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    tooltipTimerRef.current = setTimeout(() => {
      setOpenTooltip(key);
    }, 1000);
  };

  const handleTooltipMouseLeave = () => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    setOpenTooltip(null);
  };

  // Handle link tooltip with proper delay reset
  const handleLinkTooltipMouseEnter = (itemId: string) => {
    // Clear any existing timer
    if (linkTooltipTimerRef.current) {
      clearTimeout(linkTooltipTimerRef.current);
    }
    // Set new timer with delay
    linkTooltipTimerRef.current = setTimeout(() => {
      setLinkTooltipOpen(itemId);
    }, 500);
  };

  const handleLinkTooltipMouseLeave = () => {
    // Clear timer
    if (linkTooltipTimerRef.current) {
      clearTimeout(linkTooltipTimerRef.current);
    }
    // Close tooltip
    setLinkTooltipOpen(null);
  };

  // Cleanup tooltip timers on unmount
  React.useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
      if (linkTooltipTimerRef.current) {
        clearTimeout(linkTooltipTimerRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    registerSaveViewModalBridge({
      openCreate: () => setSaveViewModalMode({ type: 'create' }),
      openEdit: view => setSaveViewModalMode({ type: 'edit', view })
    });

    return () => registerSaveViewModalBridge(null);
  }, []);

  // Load saved Must Wins view preference on mount (sticky session behavior).
  // Skip if deep link is present - deep link takes precedence.
  // Moved here from ViewControls.tsx now that the Must Wins toggle lives in
  // the left nav (CSME-112991).
  React.useEffect(() => {
    if (hasRestoredMustWinPreference) {
      return;
    }
    hasRestoredMustWinPreference = true;

    if (shouldPreferUrlOverLocalPreferences()) {
      return;
    }

    const preferences = getUserPreferences();
    if (preferences.mustWinViewActive !== undefined) {
      dispatch(homeActions.setMustWinView(preferences.mustWinViewActive));
    }
  }, [dispatch, homeActions]);

  // Handler for copying Must Win deep link
  const handleCopyMustWinLink = async (item: {
    id: string;
    value: string;
    label: string;
    type: 'audience' | 'productGroup' | 'category' | 'product' | 'multi';
    filters?: {
      audiences?: string[];
      categories?: string[];
      products?: string[];
      productGroups?: string[];
    };
  }) => {
    try {
      await copyMustWinDeepLink({
        type: item.type,
        value: item.value,
        label: item.label,
        filters: item.filters,
        viewContext: {
          startDate: dateRange?.start,
          endDate: dateRange?.end
        }
      });

      // Show success toast
      ToastQueue.positive('Link copied! You can now share this filter.', {
        timeout: 3000
      });

      console.log('🔗 [Must Win Deep Link] Copied link for:', item.label);
    } catch (error) {
      console.error('Failed to copy Must Win link:', error);
      // Show error toast
      ToastQueue.negative('Failed to copy link. Please try again.', {
        timeout: 3000
      });
    }
  };

  const toggleSidebar = () => {
    dispatch(actions.toggleSidebar());
    // Sidebar state is not persisted to localStorage
  };

  const onThemeChange = event => {
    const newTheme = event ? 'darkest' : 'lightest';
    changeTheme(newTheme);
  };

  // Count how many Must Win items are selected (audience-only) for toggle vs remove-one behavior
  const selectedMustWinCount = React.useMemo(() => {
    if (!mustWinFilterOptions?.data || !audiences?.selectedAudiences?.length)
      return 0;
    return mustWinFilterOptions.data.filter(item => {
      if (item.type === 'audience') {
        return audiences.selectedAudiences.includes(item.value);
      }
      if (item.filters?.audiences?.length) {
        return (item.filters.audiences || []).every(audience =>
          audiences.selectedAudiences.includes(audience)
        );
      }
      return false;
    }).length;
  }, [mustWinFilterOptions?.data, audiences?.selectedAudiences]);

  return (
    <View
      gridArea="sidebar"
      UNSAFE_className={classNames(styles['left-sidebar-container'], {
        [styles['left-sidebar-container-collapsed']]: !isSidebarOpen
      })}
      data-testid="left-sidebar-container"
    >
      <Flex
        UNSAFE_className={classNames(styles['left-sidebar-content'], {
          [styles['left-sidebar-content-collapsed']]: !isSidebarOpen
        })}
      >
        <View>
          <Flex
            direction="column"
            gap="size-350"
            UNSAFE_className={classNames({
              [styles['left-sidebar-nav-collapsed']]: !isSidebarOpen
            })}
          >
            <Flex
              direction="row"
              UNSAFE_className={classNames(styles['left-sidebar-nav-item'], {
                [styles['left-sidebar-row-collapsed']]: !isSidebarOpen
              })}
            >
              <TooltipTrigger
                isOpen={openTooltip === 'reviewApproval'}
                onOpenChange={isOpen => {
                  if (!isOpen) setOpenTooltip(null);
                }}
                placement="top"
              >
                <div
                  onMouseEnter={() => handleTooltipMouseEnter('reviewApproval')}
                  onMouseLeave={handleTooltipMouseLeave}
                  style={{ display: 'contents' }}
                >
                  <ActionButton
                    isQuiet
                    aria-label="Review & Approval"
                    data-testid="left-sidebar-review-approval-button"
                    UNSAFE_className={classNames(
                      styles['left-sidebar-icon'],
                      styles['left-sidebar-button-base'],
                      { [styles['active']]: isReviewApprovalActive }
                    )}
                    onPress={() => {
                      // Same nav-transition cleanup as Roadmap/Milestones below,
                      // so Must Wins/Saved View highlighting doesn't linger once
                      // the user leaves for an unrelated dashboard.
                      if (mustWinViewActive) {
                        dispatch(homeActions.setMustWinView(false));
                        saveUserPreferences({ mustWinViewActive: false });
                      }
                      exitActiveSavedView();
                      dispatch(actions.setSavedViewsExpanded(false));
                      navigate('/review-approval');
                    }}
                  >
                    <span className={styles['left-sidebar-icon-badge-wrapper']}>
                      <HomeIcon
                        width={20}
                        height={20}
                        style={{ width: 20, height: 20 }}
                      />
                      {hasPendingApprovals && (
                        <span
                          className={styles['left-sidebar-icon-badge-dot']}
                          aria-label="Pending approvals"
                        />
                      )}
                    </span>
                    {isSidebarOpen && (
                      <Text UNSAFE_className={styles['left-sidebar-icon-text']}>
                        Dashboard
                      </Text>
                    )}
                  </ActionButton>
                </div>
                <Tooltip>Dashboard</Tooltip>
              </TooltipTrigger>
            </Flex>
            <Flex
              direction="row"
              UNSAFE_className={classNames(styles['left-sidebar-nav-item'], {
                [styles['left-sidebar-row-collapsed']]: !isSidebarOpen
              })}
            >
              <TooltipTrigger
                isOpen={openTooltip === 'roadmap'}
                onOpenChange={isOpen => {
                  if (!isOpen) setOpenTooltip(null);
                }}
                placement="top"
              >
                <div
                  onMouseEnter={() => handleTooltipMouseEnter('roadmap')}
                  onMouseLeave={handleTooltipMouseLeave}
                  style={{ display: 'contents' }}
                >
                  <ActionButton
                    isQuiet
                    data-testid="left-sidebar-roadmap-button"
                    UNSAFE_className={classNames(
                      styles['left-sidebar-icon'],
                      styles['left-sidebar-button-base'],
                      { [styles['active']]: isRoadmapActive }
                    )}
                    onPress={() => {
                      // Deselect Must Wins view when navigating to Roadmap
                      // (CSME-112991: no separate "off" click on Must Wins itself)
                      if (mustWinViewActive) {
                        dispatch(homeActions.setMustWinView(false));
                        saveUserPreferences({ mustWinViewActive: false });
                      }
                      // Leaving an active Saved View: reset to the default
                      // Roadmap view (filters, date range, refetch) and
                      // clear its highlight. No-ops if none is active.
                      exitActiveSavedView();
                      // Collapse the Saved Views list so its label loses the
                      // blue highlight once the user is on another nav item.
                      dispatch(actions.setSavedViewsExpanded(false));
                      navigateWithSharedViewState('/roadmap');
                    }}
                  >
                    <RoadmapIcon
                      width={20}
                      height={20}
                      style={{ width: 20, height: 20 }}
                    />
                    {isSidebarOpen && (
                      <Text UNSAFE_className={styles['left-sidebar-icon-text']}>
                        Roadmap
                      </Text>
                    )}
                  </ActionButton>
                </div>
                <Tooltip>Roadmap</Tooltip>
              </TooltipTrigger>
            </Flex>
            <Flex
              direction="row"
              UNSAFE_className={classNames(styles['left-sidebar-nav-item'], {
                [styles['left-sidebar-row-collapsed']]: !isSidebarOpen
              })}
            >
              <TooltipTrigger
                isOpen={openTooltip === 'milestones'}
                onOpenChange={isOpen => {
                  if (!isOpen) setOpenTooltip(null);
                }}
                placement="top"
              >
                <div
                  onMouseEnter={() => handleTooltipMouseEnter('milestones')}
                  onMouseLeave={handleTooltipMouseLeave}
                  style={{ display: 'contents' }}
                >
                  <ActionButton
                    isQuiet
                    data-testid="left-sidebar-milestones-button"
                    onPress={() => {
                      // Deselect Must Wins view when navigating to Milestones
                      // (CSME-112991: no separate "off" click on Must Wins itself)
                      if (mustWinViewActive) {
                        dispatch(homeActions.setMustWinView(false));
                        saveUserPreferences({ mustWinViewActive: false });
                      }
                      // Leaving an active Saved View: reset it to default now
                      // (not just clear the highlight) so that if the user
                      // comes back to Roadmap later, there's nothing stale
                      // left for it to reset — this is the single point where
                      // "leaving a saved view" is fully resolved, regardless
                      // of which nav item the user lands on next.
                      exitActiveSavedView();
                      // Collapse the Saved Views list so its label loses the
                      // blue highlight once the user is on another nav item.
                      dispatch(actions.setSavedViewsExpanded(false));
                      navigateWithSharedViewState('/milestones');
                    }}
                    UNSAFE_className={classNames(
                      styles['left-sidebar-icon'],
                      styles['left-sidebar-button-base'],
                      { [styles['active']]: isMilestonesActive }
                    )}
                  >
                    <MilestoneIcon
                      width={20}
                      height={20}
                      style={{ width: 20, height: 20 }}
                    />
                    {isSidebarOpen && (
                      <Text UNSAFE_className={styles['left-sidebar-icon-text']}>
                        Milestones
                      </Text>
                    )}
                  </ActionButton>
                </div>
                <Tooltip>SLC Milestones</Tooltip>
              </TooltipTrigger>
            </Flex>
            <Flex
              direction="row"
              UNSAFE_className={classNames(styles['left-sidebar-nav-item'], {
                [styles['left-sidebar-row-collapsed']]: !isSidebarOpen
              })}
            >
              <TooltipTrigger
                isOpen={openTooltip === 'codename'}
                onOpenChange={isOpen => {
                  if (!isOpen) setOpenTooltip(null);
                }}
                placement="top"
              >
                <div
                  onMouseEnter={() => handleTooltipMouseEnter('codename')}
                  onMouseLeave={handleTooltipMouseLeave}
                  style={{ display: 'contents' }}
                >
                  <ActionButton
                    isQuiet
                    data-testid="left-sidebar-codename-button"
                    onPress={() =>
                      window.open(
                        'https://wiki.corp.adobe.com/x/T96Dww',
                        '_blank'
                      )
                    }
                    UNSAFE_className={classNames(
                      styles['left-sidebar-icon'],
                      styles['left-sidebar-button-base']
                    )}
                  >
                    <CodeNameIcon
                      width={20}
                      height={20}
                      style={{ width: 20, height: 20 }}
                    />
                    {isSidebarOpen && (
                      <Text UNSAFE_className={styles['left-sidebar-icon-text']}>
                        Code Names
                      </Text>
                    )}
                  </ActionButton>
                </div>
                <Tooltip>Code Names</Tooltip>
              </TooltipTrigger>
            </Flex>
            <Flex
              direction="row"
              UNSAFE_className={classNames(styles['left-sidebar-nav-item'], {
                [styles['left-sidebar-row-collapsed']]: !isSidebarOpen
              })}
            >
              <div ref={mustWinsRef} className={styles['left-sidebar-section']}>
                <Flex
                  direction="column"
                  alignItems="start"
                  UNSAFE_className={styles['must-wins-wrapper']}
                >
                  <Flex
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    UNSAFE_className={styles['must-wins-container']}
                  >
                    <TooltipTrigger
                      isOpen={openTooltip === 'mustwins'}
                      onOpenChange={isOpen => {
                        if (!isOpen) setOpenTooltip(null);
                      }}
                      placement="top"
                    >
                      <div
                        onMouseEnter={() => handleTooltipMouseEnter('mustwins')}
                        onMouseLeave={handleTooltipMouseLeave}
                        style={{ display: 'contents' }}
                      >
                        <ActionButton
                          isQuiet
                          data-testid="left-sidebar-mustwins-button"
                          onPress={() => {
                            // Explicit click on the Must Wins nav option activates
                            // the Must Wins view (CSME-112991) and also drives the
                            // dropdown's expanded state - mirrors Saved Views, where
                            // the label and chevron both drive the same shared state.
                            // While the sidebar is collapsed, this label is the only
                            // visible Must Wins control, so a click here is really
                            // two actions at once: open the sidebar, then reveal the
                            // dropdown - so it always expands (like Saved Views'
                            // label, which only ever expands, never collapses).
                            // Once the sidebar is already open, this label is a peer
                            // of the chevron, so it toggles like any other expand/
                            // collapse control instead.
                            const wasSidebarOpen = isSidebarOpen;
                            if (!isSidebarOpen) {
                              toggleSidebar();
                            }
                            // Must Wins is a top-level view — it always runs on
                            // the normal shared Roadmap dataset, never on My
                            // Stuff's or a Saved View's. exitActiveSavedView()
                            // is the same "leave whatever's active and go back
                            // to the shared Roadmap state" call the Roadmap/
                            // Milestones buttons use: for a real Saved View it
                            // restores the stashed shared preferences and
                            // refetches /moments; for My Stuff it flips
                            // isMyStuffViewActive off and refetches /moments;
                            // for plain Roadmap (nothing active) it's a no-op.
                            // It also clears activeSavedViewId, so My Stuff/the
                            // Saved View stop being highlighted once Must Wins
                            // takes over.
                            exitActiveSavedView();
                            // Collapse the Saved Views list so its label loses
                            // the blue highlight once Must Wins takes over.
                            dispatch(actions.setSavedViewsExpanded(false));
                            dispatch(homeActions.setMustWinView(true));
                            saveUserPreferences({ mustWinViewActive: true });
                            dispatch(
                              actions.setMustWinsExpanded(
                                wasSidebarOpen ? !isMustWinsExpanded : true
                              )
                            );
                            // Must Wins is a Roadmap-only view (rendered inside
                            // Roadmap.tsx) - navigate there like the Roadmap/
                            // Milestones buttons do, so clicking this from another
                            // page (e.g. Milestones Grid) actually switches to it
                            // instead of silently setting state on the current page.
                            const isOnRoadmapPage =
                              location.pathname === '/' ||
                              location.pathname === '/roadmap' ||
                              location.pathname.startsWith('/moments');
                            if (!isOnRoadmapPage) {
                              navigateWithSharedViewState('/roadmap');
                            }
                          }}
                          UNSAFE_className={classNames(
                            styles['left-sidebar-icon'],
                            styles['left-sidebar-button-base'],
                            styles['must-wins-label'],
                            { [styles['active']]: isMustWinsActive }
                          )}
                        >
                          <TargetIcon
                            width={20}
                            height={20}
                            style={{ width: 20, height: 20, flexShrink: 0 }}
                          />
                          {isSidebarOpen && (
                            <Text
                              UNSAFE_className={classNames(
                                styles['left-sidebar-icon-text'],
                                styles['must-wins-text']
                              )}
                            >
                              Must Wins
                            </Text>
                          )}
                        </ActionButton>
                      </div>
                      <Tooltip>Must Wins</Tooltip>
                    </TooltipTrigger>
                    {isSidebarOpen && (
                      <ActionButton
                        isQuiet
                        data-testid="left-sidebar-mustwins-dropdown-button"
                        onPress={() => {
                          // Expand sidebar if it's closed (like the toggle button)
                          if (!isSidebarOpen) {
                            dispatch(actions.toggleSidebar());
                          }
                          // Toggle the dropdown
                          dispatch(
                            actions.setMustWinsExpanded(!isMustWinsExpanded)
                          );
                        }}
                        UNSAFE_className={classNames(
                          styles['must-wins-dropdown-button'],
                          isMustWinsExpanded && styles['active'],
                          isMustWinsExpanded &&
                            styles['must-wins-dropdown-button-expanded']
                        )}
                      >
                        <ChevronDownIcon
                          width={20}
                          height={20}
                          className={styles['must-wins-chevron']}
                        />
                      </ActionButton>
                    )}
                  </Flex>

                  {/* Expanded list - shown directly below Must Wins */}
                  {isMustWinsExpanded && isSidebarOpen && (
                    <Flex
                      direction="column"
                      alignItems="start"
                      UNSAFE_className={styles['must-wins-audience-list']}
                    >
                      {mustWinFilterOptions?.data?.length > 0 ? (
                        mustWinFilterOptions.data.map(item => {
                          // Check if item is selected (audience-only)
                          let isItemSelected = false;
                          if (item.type === 'audience') {
                            isItemSelected =
                              audiences?.selectedAudiences?.includes(
                                item.value
                              ) ?? false;
                          } else if (item.filters?.audiences?.length) {
                            isItemSelected = (
                              item.filters.audiences || []
                            ).every(audience =>
                              audiences?.selectedAudiences?.includes(audience)
                            );
                          }

                          // Show blue when Must Wins view is active AND this item's audience(s) are selected
                          const isSelected =
                            mustWinViewActive && isItemSelected;

                          return (
                            <div
                              key={item.id}
                              className={classNames(
                                styles['must-wins-audience-item'],
                                isSelected &&
                                  styles['must-wins-audience-item-selected']
                              )}
                            >
                              <div
                                className={styles['must-wins-item-content']}
                                onClick={e => {
                                  e.stopPropagation();

                                  // Toggle: if this item is already selected
                                  if (isSelected) {
                                    // Only one selected → turn off Must Wins and clear all audiences
                                    if (selectedMustWinCount === 1) {
                                      dispatch(
                                        homeActions.setMustWinView(false)
                                      );
                                      dispatch(
                                        homeActions.setMustWinDropdownSelection(
                                          null
                                        )
                                      );
                                      dispatch(
                                        homeActions.setHideEmptySwimLanes(false)
                                      );
                                      dispatch(
                                        homeActions.setSelectedAudiences([])
                                      );
                                      saveUserPreferences({
                                        mustWinViewActive: false,
                                        mustWinDropdownSelection: null,
                                        hideEmptySwimLanes: false,
                                        audiences: []
                                      });
                                      return;
                                    }
                                    // Multiple selected → remove only this item's audience(s)
                                    const toRemove =
                                      item.type === 'audience'
                                        ? [item.value]
                                        : item.filters?.audiences || [];
                                    const newAudiences = (
                                      audiences?.selectedAudiences || []
                                    ).filter(a => !toRemove.includes(a));
                                    dispatch(
                                      homeActions.setSelectedAudiences(
                                        newAudiences
                                      )
                                    );
                                    if (
                                      mustWinDropdownSelection === item.value
                                    ) {
                                      dispatch(
                                        homeActions.setMustWinDropdownSelection(
                                          null
                                        )
                                      );
                                    }
                                    saveUserPreferences({
                                      audiences: newAudiences
                                    });
                                    return;
                                  }

                                  // Apply filters for selected item (audience/category/product etc.)

                                  // Enable hiding empty swimlanes (only for Must Win dropdown)
                                  dispatch(
                                    homeActions.setHideEmptySwimLanes(true)
                                  );

                                  // Store which item was selected from Must Win dropdown (for blue highlight)
                                  dispatch(
                                    homeActions.setMustWinDropdownSelection(
                                      item.value
                                    )
                                  );

                                  // Switch to Must Wins view when user selects a Must Win option.
                                  // Exit any active Saved View/My Stuff first — see the same
                                  // exitActiveSavedView() call on the Must Wins button above for
                                  // why this is needed (Must Wins always runs on the shared
                                  // Roadmap dataset, never My Stuff's or a Saved View's).
                                  exitActiveSavedView();
                                  dispatch(homeActions.setMustWinView(true));

                                  // Save Must Win dropdown state to localStorage (for persistence across refreshes)
                                  saveUserPreferences({
                                    hideEmptySwimLanes: true,
                                    mustWinDropdownSelection: item.value,
                                    mustWinViewActive: true
                                  });

                                  // CLEAR all previous Must Win selections first (single selection mode)
                                  dispatch(
                                    homeActions.setSelectedAudiences([])
                                  );
                                  dispatch(homeActions.setSelectedCats([]));
                                  dispatch(homeActions.setSelectedProducts([]));
                                  dispatch(
                                    homeActions.setCategoryProductSelection({
                                      categories: [],
                                      products: []
                                    })
                                  );

                                  // Set filter based on item type
                                  if (item.filters) {
                                    const audiencesToApply =
                                      item.filters.audiences || [];
                                    const categoriesToApply =
                                      item.filters.categories || [];
                                    const productsToApply =
                                      item.filters.products || [];
                                    const productGroupsToApply =
                                      item.filters.productGroups || [];

                                    if (audiencesToApply.length > 0) {
                                      dispatch(
                                        homeActions.setSelectedAudiences(
                                          audiencesToApply
                                        )
                                      );
                                    }

                                    if (productGroupsToApply.length > 0) {
                                      dispatch(
                                        homeActions.setSelectedGroups(
                                          productGroupsToApply
                                        )
                                      );
                                    }

                                    let combinedProducts = [...productsToApply];
                                    if (categoriesToApply.length > 0) {
                                      categoriesToApply.forEach(category => {
                                        const childProducts =
                                          categoryToProductMap[category] || [];
                                        if (childProducts.length > 0) {
                                          combinedProducts.push(
                                            ...childProducts
                                          );
                                        }
                                      });
                                      dispatch(
                                        homeActions.setCategoryProductSelection(
                                          {
                                            categories: categoriesToApply,
                                            products: combinedProducts
                                          }
                                        )
                                      );
                                      dispatch(
                                        homeActions.setSelectedCats(
                                          categoriesToApply
                                        )
                                      );
                                    }

                                    if (combinedProducts.length > 0) {
                                      const uniqueProducts = Array.from(
                                        new Set(combinedProducts)
                                      );
                                      dispatch(
                                        homeActions.setSelectedProducts(
                                          uniqueProducts
                                        )
                                      );
                                      combinedProducts = uniqueProducts;
                                    }

                                    saveUserPreferences({
                                      audiences: audiencesToApply,
                                      categories: categoriesToApply,
                                      products: combinedProducts,
                                      productGroups: productGroupsToApply,
                                      categoryProductCategories:
                                        categoriesToApply,
                                      categoryProductProducts: combinedProducts
                                    });
                                  } else if (item.type === 'audience') {
                                    dispatch(
                                      homeActions.setSelectedAudiences([
                                        item.value
                                      ])
                                    );
                                    saveUserPreferences({
                                      audiences: [item.value],
                                      categories: [],
                                      products: [],
                                      categoryProductCategories: [],
                                      categoryProductProducts: []
                                    });
                                  } else if (item.type === 'category') {
                                    // ✅ Dynamically check if category has children using categoryToProductMap
                                    const childProducts =
                                      categoryToProductMap[item.value] || [];

                                    if (childProducts.length > 0) {
                                      // Has children: Select category + ALL child products (hierarchical selection)
                                      dispatch(
                                        homeActions.setCategoryProductSelection(
                                          {
                                            categories: [item.value],
                                            products: childProducts
                                          }
                                        )
                                      );
                                      dispatch(
                                        homeActions.setSelectedCats([
                                          item.value
                                        ])
                                      );
                                      dispatch(
                                        homeActions.setSelectedProducts(
                                          childProducts
                                        )
                                      );
                                      saveUserPreferences({
                                        categoryProductCategories: [item.value],
                                        categoryProductProducts: childProducts,
                                        categories: [item.value],
                                        products: childProducts,
                                        audiences: []
                                      });
                                    } else {
                                      // No children: Just select the category
                                      dispatch(
                                        homeActions.setCategoryProductSelection(
                                          {
                                            categories: [item.value],
                                            products: []
                                          }
                                        )
                                      );
                                      dispatch(
                                        homeActions.setSelectedCats([
                                          item.value
                                        ])
                                      );
                                      saveUserPreferences({
                                        categoryProductCategories: [item.value],
                                        categoryProductProducts: [],
                                        categories: [item.value],
                                        audiences: [],
                                        products: []
                                      });
                                    }
                                  } else if (item.type === 'product') {
                                    // Set both categoryProductSelection AND selectedProducts (like NewFilter.tsx does)
                                    dispatch(
                                      homeActions.setCategoryProductSelection({
                                        categories: [],
                                        products: [item.value]
                                      })
                                    );
                                    dispatch(
                                      homeActions.setSelectedProducts([
                                        item.value
                                      ])
                                    );
                                    saveUserPreferences({
                                      categoryProductCategories: [],
                                      categoryProductProducts: [item.value],
                                      products: [item.value],
                                      audiences: [],
                                      categories: []
                                    });
                                  } else if (item.type === 'productGroup') {
                                    dispatch(
                                      homeActions.setSelectedGroups([
                                        item.value
                                      ])
                                    );
                                    saveUserPreferences({
                                      productGroups: [item.value],
                                      audiences: [],
                                      categories: [],
                                      products: [],
                                      categoryProductCategories: [],
                                      categoryProductProducts: []
                                    });
                                  }

                                  // Must Wins is a Roadmap-only view - navigate
                                  // there so selecting an item from another page
                                  // (e.g. Milestones Grid) actually switches to it.
                                  const isOnRoadmapPage =
                                    location.pathname === '/' ||
                                    location.pathname === '/roadmap' ||
                                    location.pathname.startsWith('/moments');
                                  if (!isOnRoadmapPage) {
                                    navigateWithSharedViewState('/roadmap');
                                  }
                                }}
                              >
                                <Text>{item.label}</Text>
                              </div>
                              <div
                                className={styles['must-wins-link-wrapper']}
                                onMouseEnter={() =>
                                  handleLinkTooltipMouseEnter(item.id)
                                }
                                onMouseLeave={handleLinkTooltipMouseLeave}
                              >
                                <TooltipTrigger
                                  placement="top"
                                  isOpen={linkTooltipOpen === item.id}
                                  onOpenChange={isOpen => {
                                    if (!isOpen) {
                                      setLinkTooltipOpen(null);
                                    }
                                  }}
                                >
                                  <ActionButton
                                    isQuiet
                                    UNSAFE_className={
                                      styles['must-wins-copy-link-button']
                                    }
                                    onPress={() => handleCopyMustWinLink(item)}
                                    aria-label={`Copy link for ${item.label}`}
                                    data-testid={`must-win-copy-link-${item.id}`}
                                  >
                                    <SavedViewShareLinkIcon
                                      width={16}
                                      height={16}
                                    />
                                  </ActionButton>
                                  <Tooltip>Copy shareable link</Tooltip>
                                </TooltipTrigger>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className={styles['must-wins-audience-item']}>
                          <Text>
                            {mustWinFilterOptions.loading
                              ? 'Loading...'
                              : 'No options available'}
                          </Text>
                        </div>
                      )}
                    </Flex>
                  )}
                </Flex>
              </div>
            </Flex>
            <SavedViewsSection
              activeSavedViewId={activeSavedViewId}
              handleCopyLink={handleCopyLink}
              handleOpenSavedView={handleOpenSavedView}
              handleOpenMyStuff={handleOpenMyStuffFromNav}
            />
          </Flex>
        </View>
        <View>
          <Flex
            direction="column"
            gap="size-125"
            UNSAFE_className={classNames({
              [styles['left-sidebar-footer-collapsed']]: !isSidebarOpen
            })}
          >
            {isSidebarOpen && (
              <Flex direction="row">
                {/* Settings button and dialog are commented out for now. Enable this block to restore Settings functionality. */}
                <></>
              </Flex>
            )}
            <Flex
              justifyContent="start"
              UNSAFE_className={classNames(styles['left-sidebar-nav-item'], {
                [styles['left-sidebar-row-collapsed']]: !isSidebarOpen
              })}
            >
              <TooltipTrigger
                isOpen={openTooltip === 'toggle'}
                onOpenChange={isOpen => {
                  if (!isOpen) setOpenTooltip(null);
                }}
                placement="right"
              >
                <div
                  onMouseEnter={() => handleTooltipMouseEnter('toggle')}
                  onMouseLeave={handleTooltipMouseLeave}
                  style={{ display: 'contents' }}
                >
                  <ActionButton
                    data-testid="left-sidebar-toggle-button"
                    UNSAFE_className={classNames(
                      styles['left-sidebar-icon'],
                      styles['left-sidebar-button-base'],
                      styles['left-sidebar-toggle-button']
                    )}
                    onPress={() => toggleSidebar()}
                    isQuiet
                  >
                    {isSidebarOpen ? (
                      <LeftSidebarOpenIcon
                        width={20}
                        height={20}
                        style={{ width: 20, height: 20 }}
                      />
                    ) : (
                      <LeftSidebarCloseIcon
                        width={20}
                        height={20}
                        style={{ width: 20, height: 20 }}
                      />
                    )}
                  </ActionButton>
                </div>
                <Tooltip placement="right">
                  {isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
                </Tooltip>
              </TooltipTrigger>
            </Flex>
          </Flex>
        </View>
      </Flex>
      <SaveViewModal
        isOpen={saveViewModalMode !== null}
        savedView={
          saveViewModalMode?.type === 'edit' ? saveViewModalMode.view : null
        }
        onDismiss={() => setSaveViewModalMode(null)}
      />
    </View>
  );
};

export default LeftSidebar;
